import * as am5 from '@amcharts/amcharts5';
import * as am5xy from '@amcharts/amcharts5/xy';
import { formatNumber } from '@angular/common';
import { Injectable } from '@angular/core';
import { compile } from 'ejs';
import { isNil } from 'lodash';
import { DateTime } from 'luxon';
import * as R from 'ramda';
import { Rate } from '../../models';

@Injectable({
    providedIn: 'root',
})
export class NtlGraphBuilderService {
    constructor() {}

    buildGraph(rootEl: HTMLElement): am5xy.XYChart {
        const root: am5.Root = am5.Root.new(rootEl, {
            tooltipContainerBounds: {
                top: 300,
                bottom: 100,
                left: 100,
                right: 100,
            },
        });
        root.utc = true;

        const chart: am5xy.XYChart = root.container.children.push(
            am5xy.XYChart.new(root, {
                panY: false,
                layout: root.verticalLayout,
                maxTooltipDistance: -1,
            }),
        );

        // X-Axis
        const xAxis: am5xy.DateAxis<am5xy.AxisRenderer> = chart.xAxes.push(
            am5xy.DateAxis.new(root, {
                renderer: am5xy.AxisRendererX.new(root, {}),
                baseInterval: {
                    timeUnit: 'month',
                    count: 1,
                },
                gridIntervals: [{ timeUnit: 'month', count: 1 }],
                tooltip: am5.Tooltip.new(root, {
                    themeTags: ['axis'],
                    forceHidden: true,
                }),
                periodChangeDateFormats: {
                    month: "MMM ''yy",
                },
                dateFormats: {
                    month: "MMM ''yy",
                },
                paddingTop: 6,
            }),
        );

        const xRenderer: am5xy.AxisRenderer = xAxis.get('renderer');

        xRenderer.grid.template.setAll({
            strokeWidth: 0,
            strokeOpacity: 0,
        });

        xRenderer.labels.template.setAll({
            rotation: 60,
            dx: 25,
            fill: am5.color('#636D79'),
            fontSize: 12,
        });

        // Y-Axis
        const yAxis: am5xy.ValueAxis<am5xy.AxisRenderer> = chart.yAxes.push(
            am5xy.ValueAxis.new(root, {
                renderer: am5xy.AxisRendererY.new(root, {}),
                autoZoom: false,
                numberFormat: '$#.00',
                strictMinMax: true,
                paddingRight: 6,
            }),
        );

        const rendererY: am5xy.AxisRenderer = yAxis.get('renderer');

        rendererY.grid.template.set('forceHidden', true);

        rendererY.labels.template.set('forceHidden', true);

        // Cursor
        const cursor: am5xy.XYCursor = am5xy.XYCursor.new(root, {
            behavior: 'none',
            xAxis,
        });

        cursor.lineY.setAll({
            visible: false,
        });

        cursor.lineX.setAll({
            strokeDasharray: null,
            strokeWidth: 1,
            stroke: am5.color('#636D79'), // dat-grey-500
        });

        chart.set('cursor', cursor);

        // Line Series w/o data
        const {
            withFuelSeries: contractWithFuel,
            withoutFuelSeries: contractWithoutFuel,
        } = this._createRateSeries(
            root,
            chart,
            xAxis,
            yAxis,
            'Contract',
            '#1C60F2', // dat-blue-400
            '#1C60F2', // dat-blue-400
            '#C7D7FA', // dat-blue-100
            'contractWithFuel',
            'contractWithoutFuel',
        );

        const {
            withFuelSeries: spotWithFuel,
            withoutFuelSeries: spotWithoutFuel,
        } = this._createRateSeries(
            root,
            chart,
            xAxis,
            yAxis,
            'Spot',
            '#FFBC04', // dat-flame-400
            '#FFBC04', // dat-flame-400
            '#FFDD81', // dat-flame-200
            'spotWithFuel',
            'spotWithoutFuel',
        );

        const tooltip = this._setTooltipForSeries(spotWithFuel, chart);

        this._createLegend(
            chart,
            root,
            spotWithFuel,
            contractWithFuel,
            spotWithoutFuel,
            contractWithoutFuel,
            tooltip,
        );

        // Return
        return chart;
    }

    setYAxisRange(chart: am5xy.XYChart, rates: Rate[]): void {
        const minRate: number = Math.min(
            ...rates.map((rate: Rate) =>
                Math.min(...R.values(R.omit(['date'], rate))),
            ),
        );
        const minRateLine: number = Math.ceil(minRate * 2) / 2 - 0.5;

        const maxRate: number = Math.max(
            ...rates.map((rate: Rate) =>
                Math.max(...R.values(R.omit(['date'], rate))),
            ),
        );
        const maxRateLine: number = Math.floor(maxRate * 2) / 2 + 0.5;

        const yAxis = chart.yAxes.getIndex(
            0, // has only one y-axis (orr.epstein)
        ) as am5xy.ValueAxis<am5xy.AxisRenderer>;

        yAxis.setAll({
            min: minRateLine,
            max: maxRateLine,
        });

        yAxis.axisRanges.clear();

        this._rangeStep(minRateLine, maxRateLine, 0.5).forEach((n: number) => {
            const dataItem = yAxis.makeDataItem({
                value: n,
            });

            const range = yAxis.createAxisRange(dataItem);

            range.get('label').setAll({
                forceHidden: false,
                text: `$${formatNumber(n, 'en-US', '1.2-2')}`,
                fill: am5.color('#636D79'),
                fontSize: 12,
            });

            range.get('grid').setAll({
                forceHidden: false,
                strokeDasharray: [4],
                strokeWidth: 1,
                location: 1,
            });
        });
    }

    setData(chart: am5xy.XYChart, rates: Rate[]): void {
        chart.series.each((series: am5xy.XYSeries) => {
            series.data.setAll(this._calculateSeriesData(rates));

            if (series.isVisible()) {
                series.appear(1000);
            }
        });
    }

    _setTooltipForSeries(
        lineSeries: am5xy.LineSeries,
        chart: am5xy.XYChart,
    ): am5.Tooltip {
        const tooltip: am5.Tooltip = lineSeries.get('tooltip');

        chart.plotContainer.set('tooltipPosition', 'pointer');
        chart.plotContainer.set('tooltipHTML', '<div></div>');
        chart.plotContainer.set('tooltip', tooltip);

        tooltip.adapters.add('pointTo', (pointTo) => {
            return {
                ...pointTo,
                y: 0,
            };
        });
        tooltip.label.adapters.add('dy', (dy) => {
            lineSeries.get('tooltip').adapters.add('bounds', function () {
                return {
                    left: 60,
                    right: 1050,
                    top: 100,
                    bottom: 20,
                };
            });
            return -tooltip._fy;
        });

        this._setTooltipHTML(chart, tooltip);

        tooltip.get('background').setAll({
            strokeWidth: 1,
            fill: am5.color('#FFFFFF'),
            stroke: am5.color('#636D79'),
            strokeOpacity: 1,
            fillOpacity: 1,
        });

        return tooltip;
    }

    private _getTemplateForDataItem(
        dataContext: {
            contractWithFuel: number;
            contractWithoutFuel: number;
            date: number;
            spotWithFuel: number;
            spotWithoutFuel: number;
        },
        userData: {
            textColorHex: string;
            hasFuel: boolean;
            valueYField: string;
        },
        subtractFuelContract: boolean,
        subtractFuelSpot: boolean,
        name: string,
    ): string {
        return compile(`
            <div class="flex flex-col items-center gap-y-1 <%= colspan %>">
                <div style="color: <%= textColor %>" class="font-caption-strong">
                    <%= name %>
                </div>
                <div class="font-caption">
                    <%= date %>
                </div>
                <div class="font-caption-strong">
                    $<%= price %>
                </div>
            </div>
        `)({
            colspan: `col-span-${
                !userData.hasFuel &&
                (!subtractFuelContract || !subtractFuelSpot)
                    ? 2
                    : 1
            }`,
            textColor: userData.textColorHex,
            name,
            date: DateTime.fromMillis(dataContext.date, { zone: 'utc' }).toFormat('LLLL yyyy'),
            price: formatNumber(
                dataContext[userData.valueYField],
                'en-US',
                '1.2-2',
            ),
        });
    }

    private _getTooltipTemplate(rows: number, content: string): string {
        return compile(`
            <div class="-ml-3 -mt-1 mb-2 grid grid-cols-2 <%= rows %> gap-2">
                <%- content %>
            </div>
        `)({
            rows: `grid-rows-${rows}`,
            content,
        });
    }

    private _calculateSeriesData(rates: Rate[]): any[] {
        return rates.map((rate: Rate) => ({
            ...rate,
            date: DateTime.fromISO(rate.date, { zone: 'utc' }).toMillis(),
        }));
    }

    private _createLineSeries(
        root: am5.Root,
        chart: am5xy.XYChart,
        name: string,
        xAxis: am5xy.DateAxis<am5xy.AxisRenderer>,
        yAxis: am5xy.ValueAxis<am5xy.AxisRenderer>,
        textColorHex: string,
        lineColorHex: string,
        hasFuel: boolean,
        valueYField: string,
    ): am5xy.LineSeries {

        const tooltip =  am5.Tooltip.new(root, {
            getFillFromSprite: false,
        })
        tooltip.get('background').set('strokeOpacity', 0);

        const lineSeries: am5xy.LineSeries = chart.series.push(
            am5xy.LineSeries.new(root, {
                name,
                xAxis,
                yAxis,
                valueXField: 'date',
                valueYField,
                tooltip: tooltip,
                stroke: am5.color(lineColorHex),
                fill: am5.color(lineColorHex),
                setStateOnChildren: true,
                // userData is used to properly display tooltip (orr.epstein)
                userData: {
                    textColorHex,
                    hasFuel,
                    valueYField,
                },
            }),
        );
        lineSeries.strokes.template.setAll({
            strokeWidth: 2,
        });
        lineSeries.bullets.push((root) =>
            am5.Bullet.new(root, {
                sprite: am5.Rectangle.new(root, {
                    width: 8,
                    height: 8,
                    stroke: am5.color(lineColorHex),
                    fill: am5.color(lineColorHex),
                    dy: -4,
                    dx: -4,
                }),
            }),
        );

        return lineSeries;
    }

    private _createRateSeries(
        root: am5.Root,
        chart: am5xy.XYChart,
        xAxis: am5xy.DateAxis<am5xy.AxisRenderer>,
        yAxis: am5xy.ValueAxis<am5xy.AxisRenderer>,
        name: string,
        textColorHex: string,
        withFuelLineColorHex: string,
        withoutFuelLineColorHex: string,
        withFuelvalueYField: string,
        withoutFuelvalueYField: string,
    ): {
        withFuelSeries: am5xy.LineSeries;
        withoutFuelSeries: am5xy.LineSeries;
    } {
        const withFuelSeries = this._createLineSeries(
            root,
            chart,
            `${name} With Fuel`,
            xAxis,
            yAxis,
            textColorHex,
            withFuelLineColorHex,
            true,
            withFuelvalueYField,
        );

        const withoutFuelSeries = this._createLineSeries(
            root,
            chart,
            `${name} Without Fuel`,
            xAxis,
            yAxis,
            textColorHex,
            withoutFuelLineColorHex,
            false,
            withoutFuelvalueYField,
        );

        return { withFuelSeries, withoutFuelSeries };
    }

    private _rangeStep(start: number, end: number, step): number[] {
        return R.unfold((n) => (n <= end ? [n, n + step] : false), start);
    }

    private _createLegend(
        chart: am5xy.XYChart,
        root: am5.Root,
        spotSeries: am5xy.LineSeries,
        contractSeries: am5xy.LineSeries,
        spotWithoutFuelSeries: am5xy.LineSeries,
        contractWithoutFuelSeries: am5xy.LineSeries,
        tooltip: am5.Tooltip,
    ) {
        const legendContainer = am5.Container.new(root, {
            layout: root.horizontalLayout,
        });

        chart.children.push(legendContainer);
        var spotLegend = legendContainer.children.push(
            am5.Legend.new(root, {
                clickTarget: 'none',
                layout: root.horizontalLayout,
            }),
        );
        // Set legend marker to be a line
        spotLegend.markers.template.setAll({
            height: 3,
        });
        spotLegend.labels.template.setAll({
            fontSize: 12,
            fontWeight: '300',
            fill: am5.color('#192129'),
        });

        var spotWithoutLegend = legendContainer.children.push(
            am5.Legend.new(root, {
                clickTarget: 'marker',
                layout: root.horizontalLayout,
            }),
        );
        spotWithoutLegend.markers.template.setAll({
            height: 3,
        });

        // Disable grey-out on legend item click
        spotWithoutLegend.itemContainers.template.set(
            'setStateOnChildren',
            false,
        );

        //Sets the line color so that the grey state is not applied
        spotWithoutLegend.markerRectangles.template.setAll({
            strokeOpacity: 0,
        });
        spotWithoutLegend.markerRectangles.template.adapters.add(
            'fill',
            (fill, target) => {
                return am5.color('#FFDD81');
            },
        );

        //Add legend checkmark using svg path
        spotWithoutLegend.markers.template.setup = this._checkboxMarkers(
            root,
            tooltip,
            chart,
        );

        spotWithoutLegend.labels.template.setAll({
            fontSize: 12,
            fontWeight: '300',
            fill: am5.color('#192129'),
        });

        var contractLegend = legendContainer.children.push(
            am5.Legend.new(root, {
                clickTarget: 'none',
                layout: root.horizontalLayout,
            }),
        );
        contractLegend.markers.template.setAll({
            height: 3,
        });

        contractLegend.labels.template.setAll({
            fontSize: 12,
            fontWeight: '300',
            fill: am5.color('#192129'),
        });

        var contractWithoutLegend = legendContainer.children.push(
            am5.Legend.new(root, {
                layout: root.horizontalLayout,
                clickTarget: 'marker',
            }),
        );
        contractWithoutLegend.markers.template.setAll({
            height: 3,
        });
        contractWithoutLegend.itemContainers.template.set(
            'setStateOnChildren',
            false,
        );
        spotLegend.itemContainers.template.setAll({ paddingTop: 6 });
        spotWithoutLegend.itemContainers.template.setAll({ paddingTop: 6 });
        contractLegend.itemContainers.template.setAll({ paddingTop: 6 });
        contractWithoutLegend.itemContainers.template.setAll({ paddingTop: 6 });
        //Sets the line color so that the grey state is not applied
        contractWithoutLegend.markerRectangles.template.setAll({
            strokeOpacity: 0,
        });
        contractWithoutLegend.markerRectangles.template.adapters.add(
            'fill',
            (fill, target) => {
                return am5.color('#C7D7FA');
            },
        );
        contractWithoutLegend.markers.template.setup = this._checkboxMarkers(
            root,
            tooltip,
            chart,
        );

        contractWithoutLegend.labels.template.setAll({
            fontSize: 12,
            fontWeight: '300',
            fill: am5.color('#192129'),
        });

        spotLegend.data.setAll([spotSeries]);
        spotWithoutLegend.data.setAll([spotWithoutFuelSeries]);
        contractLegend.data.setAll([contractSeries]);
        contractWithoutLegend.data.setAll([contractWithoutFuelSeries]);
    }

    private _checkboxMarkers(
        root: am5.Root,
        tooltip: am5.Tooltip,
        chart: am5xy.XYChart,
    ): <O extends am5.Container>(entity: O) => void {
        return (marker) => {
            let check = am5.Graphics.new(root, {
                fill: am5.color('#0046e0'),
                fillOpacity: 1,
                svgPath:
                    'M -10 -7 C -8.3431 -7 -7 -5.6569 -7 -4 L -7 8 C -7 9.6569 -8.3431 11 -10 11 L -22 11 C -23.6569 11 -25 9.6569 -25 8 L -25 -4 C -25 -5.6569 -23.6569 -7 -22 -7 L -10 -7 Z M -11.5303 -2.0303 L -18 4.4393 L -20.4697 1.9697 C -20.7626 1.6768 -21.2374 1.6768 -21.5303 1.9697 C -21.8232 2.2626 -21.8232 2.7374 -21.5303 3.0303 L -18.5303 6.0303 C -18.2374 6.3232 -17.7626 6.3232 -17.4697 6.0303 L -10.4697 -0.9697 C -10.1768 -1.2626 -10.1768 -1.7374 -10.4697 -2.0303 C -10.7626 -2.3232 -11.2374 -2.3232 -11.5303 -2.0303 Z',
            });
            check.states.create('disabled', {
                fill: am5.color('#fff'),
                fillOpacity: 1,
                stroke: am5.color('#636D79'),
                strokeWidth: 2,
                svgPath:
                    'M -10 -3.8 Z Z M -21.7 -6.5 H -9.1 C -8.11 -6.5 -7.3 -5.69 -7.3 -4.7 V 7.9 C -7.3 8.89 -8.11 9.7 -9.1 9.7 H -21.7 C -22.69 9.7 -23.5 8.89 -23.5 7.9 V -4.7 C -23.5 -5.69 -22.69 -6.5 -21.7 -6.5 Z',
            });

            check.events.on('click', async (ev) => {
                this._setTooltipHTML(chart, tooltip);
            });
            marker.children.push(check);
        };
    }

    private _setTooltipHTML(chart: am5xy.XYChart, tooltip: am5.Tooltip) {
        const [cwf, cwof, swf, swof] = chart.series.values;
        tooltip.label.adapters.add('html', (_, target) => {
            const rows: number = cwof.isVisible() || swof.isVisible() ? 2 : 1;
            const content: string = [swf, cwf, swof, cwof]
                .map((series: am5xy.XYSeries) => {
                    const item = target.dataItem;

                    return !isNil(item) && series.isVisible()
                        ? this._getTemplateForDataItem(
                            item.dataContext as any,
                            series.get('userData'),
                            cwof.isVisible(),
                            swof.isVisible(),
                            series.get('name')
                        )
                        : '';
                })
                .reduce(R.concat, '');

            return this._getTooltipTemplate(rows, content);
        }
        );
    }
}
